<template>
  <component
    :is="currentInputMode.component()"
    :value="internalValue"
    :disable-camera-input="disableCameraInput"
    :computed-rules="computedRules"
    :background-color="inputCompleted ? 'successPastel' : ''"
    :append-icon="inputCompleted ? 'check' : ''"
    v-bind="$attrs"
    @keydown="onKey"
    @input="bubbleInput"
    @valid-scanned="inputComplete($event, false)"
    @not-supported="notSupported = true"
    @key-enter-pressed="onKey"
    @scan-complete="scanComplete"
  >
    <template v-if="showInputToggle" #append-outer>
      <bm-btn button-type="secondary" icon @click="changeInputType">
        <v-icon>
          {{ currentInputMode.icon }}
        </v-icon>
      </bm-btn>
    </template>
    <template #scanner>
      <slot name="scanner" />
      <bm-alert v-if="notSupported" type="error">{{
        $m.containers_camera_not_supported
      }}</bm-alert>
    </template>
    <template #action>
      <slot name="action" />
    </template>
  </component>
</template>

<script lang="ts">
import BmAlert from "@/components/controls/BmAlert.vue";
import BmText from "@/components/controls/input/BmText.vue";
import BmBarcodeCamera from "@/components/controls/device/barcodes/BmBarcodeCamera.vue";
import BmBarcodeLaser from "@/components/controls/device/barcodes/BmBarcodeLaser.vue";
import BmBarcodeTextInput from "@/components/controls/device/barcodes/BmBarcodeTextInput.vue";
import type { ValidationRule } from "@/plugins/validation";
import { EnumBrewmanBarcodeType } from "@/app/services/generated/enums/enumBrewmanBarcodeType.generated";
import appStore from "@/store/modules/appStore";
import { EnumSnackbarType } from "@/components/controls/snackbar/SnackbarTypes";
import type { PropType } from "vue";
import Vue from "vue";
import { translateWithParams } from "@/plugins/i18n";
import { barcodeFailure } from "@/assets/sounds/barcodeSounds";
import { validateBrewmanBarcode } from "@/plugins/bmBarcodeValidator";

export enum BmBarcodeTextMode {
  Text = "Text",
  Scanner = "Scanner",
  Laser = "Laser",
}

export function stripUrlPartsFromBarcode(urlString: string): string {
  try {
    const url = new URL(urlString);
    if (url.protocol === "http:" || url.protocol === "https:") {
      urlString = url.pathname;
      urlString = urlString.substring(urlString.lastIndexOf("/") + 1);
    }
    return urlString;
  } catch (_) {
    // Ignore non-urls
    return urlString;
  }
}

export default Vue.extend({
  name: "BmBarcodeInput",
  components: {
    BmText,
    BmBarcodeCamera,
    BmBarcodeLaser,
    BmBarcodeTextInput,
    BmAlert,
  },
  props: {
    barcodeTypes: {
      type: Array as PropType<EnumBrewmanBarcodeType[]>,
      default: () => [EnumBrewmanBarcodeType.none],
    },
    value: {
      type: String,
      default: "",
    },
    lockedMode: {
      type: String as PropType<BmBarcodeTextMode | null>,
      default: null,
    },
    disableCameraInput: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      internalValue: this.value,
      inputMode: BmBarcodeTextMode.Text,
      BmBarcodeTextMode: BmBarcodeTextMode,
      notSupported: false,
      inputCompleted: false,
      completeAnimationTimeoutRef: 0,
      scannerStates: [
        {
          type: BmBarcodeTextMode.Text,
          icon: "keyboard",
          component: () => BmBarcodeTextInput,
        },
        {
          type: BmBarcodeTextMode.Scanner,
          icon: "photo_camera",
          component: () => BmBarcodeCamera,
        },
        {
          type: BmBarcodeTextMode.Laser,
          icon: "crop_free",
          component: () => BmBarcodeLaser,
        },
      ],
    };
  },
  computed: {
    currentInputMode(): any {
      let inputMode = BmBarcodeTextMode.Text;
      if (this.lockedMode !== null) {
        // If specified only one, then use that
        inputMode = this.lockedMode;
      } else {
        // If we have a preference set
        inputMode = this.inputMode;
      }

      return (
        this.scannerStates.find((ss) => ss.type === inputMode) ??
        this.scannerStates[0]
      );
    },
    showInputToggle(): any {
      return this.lockedMode === null;
    },
    computedRules(): ValidationRule[] {
      let propRules = (this.$attrs.rules as unknown as ValidationRule[]) ?? [];
      let rules = this.$ruleSet.isBrewmanBarcodeOfTypes(this.barcodeTypes);
      return rules ? [rules, ...propRules] : [...propRules];
    },
  },
  watch: {
    value: {
      // @ts-ignore
      handler: "valueWatch",
      immediate: true,
    },
  },
  mounted() {
    const storedPreference = localStorage
      .getItem("barcode-scanner-preference")
      ?.toLowerCase();

    // Get preference on load or scanner
    this.inputMode =
      [
        BmBarcodeTextMode.Text,
        BmBarcodeTextMode.Scanner,
        BmBarcodeTextMode.Laser,
      ].find((mode) => mode.toString().toLowerCase() === storedPreference) ??
      BmBarcodeTextMode.Text;
  },
  methods: {
    changeInputType() {
      switch (this.inputMode) {
        case BmBarcodeTextMode.Scanner:
          this.inputMode = BmBarcodeTextMode.Text;
          break;
        case BmBarcodeTextMode.Text:
          this.inputMode = BmBarcodeTextMode.Laser;
          break;
        case BmBarcodeTextMode.Laser:
          this.inputMode = BmBarcodeTextMode.Scanner;
          break;
      }
      // Update device preference
      localStorage.setItem(
        "barcode-scanner-preference",
        this.inputMode.toString().toLowerCase()
      );
    },
    bubbleInput(newValue: string) {
      this.internalValue = newValue;
      this.$emit("input", newValue);
    },
    valueWatch() {
      this.internalValue = this.value;
    },
    onKey(event: KeyboardEvent) {
      if (event.key === "Enter") {
        this.inputComplete(stripUrlPartsFromBarcode(this.internalValue), true);
      }
    },
    inputComplete(barcode: string, fromScan: boolean = false) {
      // we cannot wait for v-model update to propagate back to us (so we can update our children) as it's too slow. Form
      // validation will likely occur as a result of 'input-complete' and our controls need to see the most recent value.

      this.$emit("input", barcode); // Update bound v-model

      this.$nextTick(() => {
        // Required so input-complete is fired after the input event
        const errors = this.computedRules
          .map((rule) => rule(barcode))
          .filter((x) => typeof x === "string") as string[];

        if (errors.length) {
          appStore.pushSnackbarConfig({
            text: translateWithParams("scanner_errors", {
              errors: errors.join(", "),
              barcode,
            }),
            timeout: 2000,
            type: EnumSnackbarType.warning,
          });

          //Clear internal state
          this.internalValue = "";
          return;
        }

        this.$emit("input-complete", barcode); // Inform that we have a full input (scan or manual)

        this.inputCompleted = true;
        clearTimeout(this.completeAnimationTimeoutRef);
        //("+" causes a toPrimative call with "number" hint)
        this.completeAnimationTimeoutRef = +setTimeout(() => {
          this.inputCompleted = false;
        }, 2000);

        // Store last action type to better preserve expected behaviours for users
        if (fromScan) {
          this.$emit("valid-scanned", barcode); // In case anyone cares if it was scanned rather than entered.

          appStore.pushSnackbarConfig({
            text: translateWithParams("scanned_barcode_prompt", {
              barcode,
            }),
            timeout: 1000,
            type: EnumSnackbarType.info,
          });
        }

        this.internalValue = "";
      });
    },
    async playFailureSound() {
      try {
        await new Audio(barcodeFailure).play();
      } catch (e) {
        // leave empty - no need to throw error/alert user
      }
    },
    /**
     * Strips url from barcode and passes to inputComplete
     */
    scanComplete(barcode: string | null) {
      if (barcode !== null) {
        const stripped = stripUrlPartsFromBarcode(barcode);

        // There is very inconsistent handling of barcode errors and rules handed down to some scanners not others
        // Laser scanner follows this path, and we were having problems where rubbish scans were not being
        // noticed, so here we will play a sound if the scan is probably not wanted.
        // Note that many scans will interpret as V6PermLabel, so for now we will bleep on those
        // (only possibly used during migration so not an ongoing problem)
        const type = validateBrewmanBarcode(stripped);
        if (type.isNone() || type.isV6PermLabel()) {
          // Initiate Failure Sound playing - async but deliberately do not wait for finish
          this.playFailureSound();
        }

        this.inputComplete(stripped, true);
      }
    },
  },
});
</script>
